<script setup>
// core
import {reactive, getCurrentInstance, ref, onBeforeMount} from "vue";
import {useWindowSize} from '@vueuse/core'
import {useStore} from "vuex";

// api
import {getSubscriptionsBySystem, getSubsPaymentHistory, cancelSubscriptionByType} from "@/services/Subscriptions/api";
import {SUBSCRIPTIONS_PAYMENT_SYSTEMS} from "@/services/Subscriptions/consts";

// components
import SubscriptionMainBlock from "@/components/Subscriptions/SubscriptionMainBlock.vue";
import SubscriptionTable from "@/components/Subscriptions/SubscriptionTable.vue";
import SubscriptionMobileTable from "@/components/Subscriptions/SubscriptionMobileTable.vue";
import InfoDataTextRecurring from "@/components/Recurring/InfoDataTextRecurring.vue";
import BasePagination from "@/components/ui/BasePagination.vue";
import BaseDialog from "@/components/ui/BaseDialog.vue"
import SubPaymentHistoryTable from "@/components/Subscriptions/SubPaymentHistoryTable.vue";
import SubPaymentNumbers from "@/components/Subscriptions/SubPaymentNumbers.vue";
import ButtonLoading from "@/components/Recurring/ButtonLoading.vue";

const {proxy} = getCurrentInstance();
const {width} = useWindowSize();
const isHistoryDialogVisible = ref(false)
const isCancelDialogVisible = ref(false);
const store = useStore()

const subscriptionsData = reactive({
  [SUBSCRIPTIONS_PAYMENT_SYSTEMS.STRIPE]: {
    loading: false,
    data: [],
    meta: {},
    count: 0
  },
  [SUBSCRIPTIONS_PAYMENT_SYSTEMS.WAYFORPAY]: {
    loading: false,
    data: [],
    meta: {},
    count: 0
  },
  [SUBSCRIPTIONS_PAYMENT_SYSTEMS.INVOICE]: {
    loading: false,
    data: [],
    meta: {},
    count: 0
  }
});
const paymentHistoryData = reactive({
  loading: false,
  data: [],
  meta: {},
  numbers: 0,
  subscription_id: null,
  system: null
});
const cancelSubData = reactive({
  subscription_id: null,
  system: null,
  loading: false
})

// ------ SUBSCRIPTIONS ------ //
const getSubscriptions = async (paySystem, page = 1) => {
  try {
    subscriptionsData[paySystem].loading = true;
    const {data, meta} = await getSubscriptionsBySystem(
        {type: paySystem, page},
        proxy
    );

    subscriptionsData[paySystem].count = meta.total;
    subscriptionsData[paySystem].meta = meta;
    subscriptionsData[paySystem].data = page === 1
        ? data.value
        : [...subscriptionsData[paySystem].data, ...data.value];
  } catch (error) {
    console.error(`Error fetching ${paySystem} subscriptions:`, error);
  } finally {
    subscriptionsData[paySystem].loading = false;
  }
};
const loadMore = async (paySystem) => {
  const currentPage = subscriptionsData[paySystem].meta.current_page || 1;
  const totalPages = subscriptionsData[paySystem].meta.last_page || 1;

  if (currentPage < totalPages && !subscriptionsData[paySystem].loading) {
    await getSubscriptions(paySystem, currentPage + 1);
  }
};
const changePage = async (page, paySystem) => {
  try {
    subscriptionsData[paySystem].loading = true;
    const {data, meta} = await getSubscriptionsBySystem(
        {type: paySystem, page},
        proxy
    );

    subscriptionsData[paySystem].count = meta.total;
    subscriptionsData[paySystem].meta = meta;
    subscriptionsData[paySystem].data = data.value;
  } catch (error) {
    console.error(`Error fetching ${paySystem} subscriptions:`, error);
  } finally {
    subscriptionsData[paySystem].loading = false;
  }
};
// ------ SUBSCRIPTIONS ------ //

// ------ PAYMENT HISTORY ------ //
const getHistory = async (type, subscription_id, page = 1) => {
  try {
    paymentHistoryData.loading = true
    const {history, meta, getNumbersCount} = await getSubsPaymentHistory(
        {type, subscription_id, page},
        proxy
    );

    paymentHistoryData.subscription_id = subscription_id;
    paymentHistoryData.system = type;
    paymentHistoryData.meta = meta;
    paymentHistoryData.data = page === 1
        ? history.value
        : [...paymentHistoryData.data, ...history.value];
    paymentHistoryData.numbers = getNumbersCount(paymentHistoryData.data);

  } catch (error) {
    console.error(`Error fetching history for ${type}:`, error);
  } finally {
    paymentHistoryData.loading = false;
  }
}
const loadMoreHistory = async (system, subscription_id) => {
  const currentPage = paymentHistoryData.meta.current_page || 1;
  const totalPages = paymentHistoryData.meta.last_page || 1;

  if (currentPage < totalPages && !paymentHistoryData.loading) {
    await getHistory(system, subscription_id, currentPage + 1);
  }
}
// ------ PAYMENT HISTORY ------ //

// ------ ACTIONS ------ //
const showCancelConfirm = (id, system) => {
  cancelSubData.system = system
  cancelSubData.subscription_id = id;
  isCancelDialogVisible.value = true;
  // open dialog
}

const cancelSubscription = async () => {
  cancelSubData.loading = true
  try {
    await cancelSubscriptionByType({
      subscription_id: cancelSubData.subscription_id,
      type: cancelSubData.system
    }, proxy)
    subscriptionsData[cancelSubData.system].data =
        subscriptionsData[cancelSubData.system].data.filter(s => s.subscription_id !== cancelSubData.subscription_id)
  } finally {
    cancelSubData.loading = false
    closeCancelDialog();
  }
}

const showSubHistory = async (sub, type) => {
  isHistoryDialogVisible.value = true;
  await getHistory(type, sub.subscription_id)
}
const closeHistoryDialog = () => {
  paymentHistoryData.system = null;
  paymentHistoryData.meta = {};
  paymentHistoryData.data = [];
  paymentHistoryData.numbers = 0;
  paymentHistoryData.subscription_id = null;
  isHistoryDialogVisible.value = false;
}

const closeCancelDialog = () => {
  cancelSubData.system = null
  cancelSubData.subscription_id = null;
  cancelSubData.loading = false;
  isCancelDialogVisible.value = false
}
// ------ ACTIONS ------ //

// ------ HOOKS ------ //
onBeforeMount(async () => {
  if (store.getters['auth/isLoggedIn']) {
    const promises = Object.keys(subscriptionsData).map(system =>
        getSubscriptions(system)
    );
    await Promise.allSettled(promises);

  }
});
// ------ HOOKS ------ //
</script>

<template>
  <div class="subscriptions-page">
    <h1 class="subscriptions-page__title">{{ $t('page.subscriptions.title') }}</h1>

    <div class="subscriptions-page__content">
      <SubscriptionMainBlock
          v-for="system in Object.keys(subscriptionsData)"
          :key="system"
          :title="system"
          :count="subscriptionsData[system].count"
      >
        <template v-if="width > 768">
          <SubscriptionTable
              :data="subscriptionsData[system].data"
              :system="system"
              @load-more="loadMore(system)"
              @delete="(e) => showCancelConfirm(e, system)"
              @view-history="(e) => showSubHistory(e, system)"
          >
            <template #information_column>
              <InfoDataTextRecurring
                  v-if="subscriptionsData[system].loading"
                  value="loading"
                  height="120px"
              />
              <InfoDataTextRecurring
                  v-if="!subscriptionsData[system].data.length && !subscriptionsData[system].loading"
                  value="noData"
                  height="120px"
              />
            </template>
          </SubscriptionTable>
        </template>
        <template v-else>
          <InfoDataTextRecurring
              v-if="subscriptionsData[system].loading"
              value="loading"
              height="120px"
          />
          <SubscriptionMobileTable
              v-else
              :data="subscriptionsData[system].data"
              @delete="(e) => showCancelConfirm(e, system)"
              @view-history="(e) => showSubHistory(e, system)"
          />
          <InfoDataTextRecurring
              v-if="!subscriptionsData[system].data.length && !subscriptionsData[system].loading"
              value="noData"
              height="120px"
          />
          <BasePagination
              class="subscriptions-page__pagination"
              :lastPage="subscriptionsData[system].meta.last_page"
              :currentPage="subscriptionsData[system].meta.current_page"
              @onChangePage="(numberPage) => changePage(numberPage, system)"
          />
        </template>
      </SubscriptionMainBlock>
    </div>
  </div>

  <!-- History dialog -->
  <BaseDialog
      v-model="isHistoryDialogVisible"
      size="widest"
      @close="closeHistoryDialog"
  >
    <template #header>
      <h4>{{ $t('page.subscriptions.history.title') }}</h4>
    </template>
    <template #default>
      <div class="history">
        <div v-if="paymentHistoryData.numbers.length" class="history__title">
          {{ $t('page.subscriptions.history.numbers', {count: paymentHistoryData.numbers.length}) }}
        </div>
        <div class="history__content">
          <SubPaymentNumbers :numbers="paymentHistoryData.numbers"/>
          <SubPaymentHistoryTable
              :data="paymentHistoryData.data"
              @load-more="loadMoreHistory(paymentHistoryData.system, paymentHistoryData.subscription_id)"
          >
            <template #information_column>
              <InfoDataTextRecurring
                  v-if="paymentHistoryData.loading"
                  value="loading"
                  height="120px"
              />
              <InfoDataTextRecurring
                  v-if="!paymentHistoryData.data.length && !paymentHistoryData.loading"
                  value="noData"
                  height="120px"
              />
            </template>
          </SubPaymentHistoryTable>
        </div>
      </div>
    </template>
  </BaseDialog>
  <!-- History dialog -->

  <!-- Cancel dialog -->
  <BaseDialog
      v-model="isCancelDialogVisible"
      size="standard"
      :closeOnClickOutside="false"
      @close="closeCancelDialog"
  >
    <template #header>
      <h4>{{ $t('page.subscriptions.cancel.title') }}</h4>
    </template>
    <template #default>
      <div class="cancel">
        <p class="cancel__title">{{ $t('page.subscriptions.cancel.confirm') }}</p>
        <div class="cancel__actions">
          <ButtonLoading :loading="cancelSubData.loading" @click="cancelSubscription">
            {{ $t('page.subscriptions.cancel.button') }}
          </ButtonLoading>
        </div>
      </div>
    </template>
  </BaseDialog>
  <!-- Cancel dialog -->
</template>

<style scoped lang="scss">

.subscriptions-page {
  margin: 20px;

  &__title {
    text-transform: uppercase;
    font-size: 24px;
    font-weight: 400;
    margin-bottom: 29px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__pagination {
    padding: 12px 0 0 0;
  }
}

.history {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &__title {
    color: var(--text-third-color, #656565);
    text-align: center;
    font-family: Gilroy, sans-serif;
    font-size: 15px;
    font-style: normal;
    font-weight: 500;
    line-height: 1.6;
  }

  &__content {
    padding: 0 15px;
  }
}

.cancel {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__title {
    color: var(--text-third-color, #656565);
    text-align: center;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    text-transform: uppercase;
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 16px;
  }
}
</style>
